import type { HistoryProcessFormRequest } from './validationSchema';
import type { ChangeEvent } from 'react';
import { useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { Box, Collapse } from '@mui/material';
import dayjs from 'dayjs';
import { Dialog } from '@components/Dialog';
import { ErrorBoundary } from '@components/ErrorBoundary';
import { Button } from '@components/buttons';
import { SecureSubmitButton } from '@components/buttons/SecureSubmitButton';
import { AttachmentFile, FileUpload, PDFPreviewModal } from '@pages/customer/common/file';
import { useStore } from '@stores/RootStore';
import { fonts } from '@theme/fontsCustomer';
import type { SubscriptionRequestHistoryCreateDTO } from '@repositories/subscriptionRequestRepository/subscriptionRequest.types';
import type { FileAttachment, PdfFilePreview } from '@customHooks/useFileDialog';
import { useCreateSubscriptionRequestHistoryMutation } from '@queryHooks/useSubscriptionRequest';
import type { SubscriptionRequestHistoryDTOModel } from '@repositories/subscriptionRequestRepository/models/SubscriptionRequestHistoryDTOModel';
import { useHistoryProcessForm } from './validationSchema';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  latestHistory: SubscriptionRequestHistoryDTOModel;
  showPdfPreviewFn: (fileId: string) => void;
  downloadFileFn?: (fileId: string) => Promise<void>;
};

export type ProcessBtnType = 'APPROVED' | 'REJECTED' | null;
export const SubscriptionRequestHistoryProcessModal = (props: Props) => {
  const { isOpen, onClose, latestHistory, showPdfPreviewFn, downloadFileFn } = props;

  const { t } = useTranslation();
  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useHistoryProcessForm();

  const {
    authStore,
    uiStore: { toastStore },
  } = useStore();

  const { mutate: createSubscriptionRequestHistory, isPending } = useCreateSubscriptionRequestHistoryMutation();

  const btnType = watch('btnType');
  const comment = watch('comment');

  const btnError = errors.btnType;
  // 첨부파일 관리
  const [attachments, setAttachments] = useState<FileAttachment[]>([]);
  const [pdfPreview, setPdfPreview] = useState<PdfFilePreview>({
    open: false,
    file: null,
    fileId: null,
    fileName: '',
    numPages: 0,
  });

  const handleChangePdfPreview = (pdfPreview: PdfFilePreview) => {
    setPdfPreview(pdfPreview);
  };

  const errorMessage =
    btnType === 'APPROVED'
      ? 'Purchase_History.Processing_Quotation_Approve_Failed'
      : 'Purchase_History.Processing_Quotation_Reject_Failed';

  const validation = {
    success: (data: HistoryProcessFormRequest) => {
      const subscriptionRequestId = latestHistory.subscriptionRequestId;

      const lastHistoryAttachments = latestHistory.attachments[0];
      const files = new File([], `${lastHistoryAttachments?.fileName}.${lastHistoryAttachments?.fileExtension}`);

      const rqData: SubscriptionRequestHistoryCreateDTO = {
        managerId: authStore.id,
        subscriptionRequestId,
        comment: data.comment,
        status: btnType === 'APPROVED' ? 'APPROVED' : 'REJECTED',
        files: btnType === 'APPROVED' ? data.files : [files],
      };

      createSubscriptionRequestHistory(
        {
          ...rqData,
        },
        {
          onSuccess: () => {
            const successMessage =
              btnType === 'APPROVED'
                ? 'Purchase_History.Processing_Quotation_Approve_Complete'
                : 'Purchase_History.Processing_Quotation_Reject_Complete';
            toastStore.open(t(successMessage));
            reset(
              {
                btnType: null,
                comment: '',
                files: [],
              },
              {
                keepValues: false,
              },
            );
            setAttachments([]);
            setPdfPreview({
              open: false,
              file: null,
              fileId: null,
              fileName: '',
              numPages: 0,
            });
            onClose();
          },
          onError: error => {
            console.error('🚀 ~ SubscriptionRequestHistoryProcessModal ~ onError:', error);
            toastStore.open(t(errorMessage));
          },
        },
      );
    },
    error: (errors: any) => {
      console.error('❌ Form validation failed:', errors);
      toastStore.open(t(errorMessage));
    },
  };

  const handleChangeAttachments = (attachments: FileAttachment[]) => {
    setAttachments(attachments);
    setValue(
      'files',
      attachments.map(attachment => attachment.file),
    );
  };

  return (
    <Dialog open={isOpen} onClose={onClose} title={t('Purchase_History.Quotation_Review')} size="medium">
      <ErrorBoundary key={isOpen ? 'open' : 'closed'}>
        <Form onSubmit={handleSubmit(validation.success, validation.error)}>
          <Element>
            <InfoWrap>
              <InfoItem>
                <InfoText>{t('Purchase_History.Processing_Date')}</InfoText>
                <InfoValue>{dayjs(latestHistory.createdDate).format('YYYY.MM.DD')}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoText>{t('Purchase_History.Processing_Manager')}</InfoText>
                <InfoValue>{latestHistory.managerName ?? '-'}</InfoValue>
              </InfoItem>
            </InfoWrap>
            <InfoWrap>
              <InfoItem>
                <InfoText>{t('Purchase_History.Processing_Content')}</InfoText>
                <InfoValue>{latestHistory.comment ?? '-'}</InfoValue>
              </InfoItem>
            </InfoWrap>
          </Element>
          <Element>
            <AttachmentInfoWrap>
              <InfoText>{t('Purchase_History.Processing_Attachment')}</InfoText>
              <AttachmentFileWrapper>
                {latestHistory?.attachments?.map(attachment => (
                  <AttachmentFile
                    key={attachment.fileId}
                    attachment={{
                      fileId: attachment.fileId,
                      fileName: attachment.fileName,
                      fileExtension: attachment.fileExtension ?? '',
                      file: new File([], `${attachment.fileName}.${attachment.fileExtension}`),
                    }}
                    showPdfPreviewFn={showPdfPreviewFn}
                    downloadFileFn={downloadFileFn}
                    enabledDeleteFile={false}
                    enabledDownload
                  />
                ))}
              </AttachmentFileWrapper>
            </AttachmentInfoWrap>
          </Element>
          <Element>
            <Hr />
            <ApproveRejectWrap hasError={!!btnError}>
              <Controller
                control={control}
                name="btnType"
                render={({ field: { onChange, value } }) => (
                  <>
                    <ApproveRejectItem selected={value === 'APPROVED'} onClick={() => onChange('APPROVED')}>
                      <ApproveRejectText selected={value === 'APPROVED'}>
                        {t('Purchase_History.Processing_Approve')}
                      </ApproveRejectText>
                    </ApproveRejectItem>
                    <ApproveRejectItem selected={value === 'REJECTED'} onClick={() => onChange('REJECTED')}>
                      <ApproveRejectText selected={value === 'REJECTED'}>
                        {t('Purchase_History.Processing_Rejected')}
                      </ApproveRejectText>
                    </ApproveRejectItem>
                  </>
                )}
              />
              {btnError?.message && <ErrorText>{t(btnError.message)}</ErrorText>}
            </ApproveRejectWrap>
          </Element>
          <Collapse in={btnType === 'APPROVED'} timeout={500}>
            <Element>
              <FileUploadWrap>
                <MainInfoText>{t('Purchase_History.Processing_Attachment')}</MainInfoText>
                <FileUpload
                  attachments={attachments}
                  updateAttachments={handleChangeAttachments}
                  updatePdfPreview={handleChangePdfPreview}
                />
              </FileUploadWrap>
            </Element>
          </Collapse>

          <Element>
            <TextAreaWrap>
              <TextAreaInfo>
                <MainInfoText>{t('Purchase_History.Processing_Content')}</MainInfoText>
                <MaxLength>{`(${comment?.length ?? 0}/200)`}</MaxLength>
              </TextAreaInfo>
              <Controller
                control={control}
                name="comment"
                render={({ field: { onChange, value } }) => (
                  <TextArea
                    maxLength={200}
                    placeholder={t('Purchase_History.Processing_Content_Placeholder')}
                    value={value ?? ''}
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
                  />
                )}
              />
            </TextAreaWrap>
          </Element>

          <Footer>
            <SecureSubmitButton variant="contain" size="extraLarge" type="submit" isPending={isPending}>
              {t('Purchase_History.Processing_Send')}
            </SecureSubmitButton>
            <Button variant="outline" size="extraLarge" type="button" onClick={onClose}>
              {t('Purchase_History.Processing_Cancel')}
            </Button>
          </Footer>
        </Form>
        <PDFPreviewModal pdfPreview={pdfPreview} updatePdfPreview={setPdfPreview} />
      </ErrorBoundary>
    </Dialog>
  );
};

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin: 5px 0 17px 0;
  padding-bottom: 94px;
`;

const Element = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const InfoWrap = styled(Box)`
  display: flex;
  gap: 8px;
`;

const InfoItem = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
`;

const InfoValue = styled.p`
  ${fonts.Headline8};
  color: ${({ theme }) => theme.colors['text-gray-main']};
`;

const InfoText = styled.strong`
  ${fonts.Headline9};
  color: ${({ theme }) => theme.colors['text-gray-sub-dark']};
`;

const TextAreaWrap = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const TextAreaInfo = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const MainInfoText = styled.strong`
  ${fonts.Headline8};
  color: ${({ theme }) => theme.colors['text-gray-main']};
`;

const MaxLength = styled.span`
  ${fonts.Body4};
  color: ${({ theme }) => theme.colors['text-gray-light']};
`;

const TextArea = styled.textarea`
  width: 100%;
  height: 120px;
  padding: 5px 11px;
  resize: none;
  background-color: ${({ theme }) => theme.colors['bg-gray-lighter']};
  border: 1px solid ${({ theme }) => theme.colors['border-gray-light']};
  border-radius: 2px;
  ${fonts.Body2};
`;

const FileUploadWrap = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Footer = styled(Dialog.Footer)`
  display: flex;
  padding: 0 40px;
`;

const AttachmentInfoWrap = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const AttachmentFileWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Hr = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${({ theme }) => theme.colors['border-gray-w-lighter']};
`;

const ApproveRejectWrap = styled(Box, {
  shouldForwardProp: prop => prop !== 'hasError',
})<{ hasError: boolean }>`
  position: relative;
  display: flex;
  border-radius: 3px;
  width: 100%;
  border: 1px solid
    ${({ hasError, theme }) => (hasError ? theme.colors['text-red-light'] : theme.colors['border-gray-light'])};

  & > :first-of-type {
    border-right: 1px solid ${({ theme }) => theme.colors['border-gray-light']};
  }
`;

const ApproveRejectItem = styled(Box)<{ selected: boolean }>`
  display: flex;
  width: 50%;
  height: 48px;
  padding: 8px 24px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  background-color: ${({ selected, theme }) =>
    selected ? theme.colors['bg-primary'] : theme.colors['bg-gray-lighter']};
  cursor: pointer;

  &:hover {
    background-color: ${({ selected, theme }) =>
      selected ? theme.colors['bg-primary-dark'] : theme.colors['bg-gray-light']};
  }
`;

const ApproveRejectText = styled.p<{ selected: boolean }>`
  color: ${({ selected, theme }) => (selected ? theme.colors['text-white'] : theme.colors['text-gray-main'])};
  text-align: center;
  ${fonts.Button3};
`;

const ErrorText = styled.p`
  position: absolute;
  top: 50px;
  ${fonts.Body4}
  border-right: none !important;
  color: ${({ theme }) => theme.colors['text-red-light']};
  margin-top: 4px;
  margin-bottom: 0;
`;
